import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, ArrowRight, ShieldCheck, Zap, Sparkles, Layers, FileText } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { LeadMagnet } from "@/components/site/LeadMagnet";
import { StartupPricingCallout } from "@/components/site/StartupPricingCallout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { startupFaqs } from "@/lib/finendra-data";
import {
  generateServiceSchema,
  generateFaqSchema,
  generateBreadcrumbSchema,
} from "@/components/site/Schema";

const stageMatrix = [
  {
    stage: "Pre-Seed ($0 - $50K MRR)",
    accounting: "Cash basis bookkeeping",
    reporting: "Monthly / Quarterly P&L + Balance Sheet",
    payroll: "Founder payroll + basic contractor 1099s",
    tax: "Schedule C / Form 1120 prep support",
    tools: ["Mercury", "Brex", "QuickBooks Online", "Gusto"],
    plan: "Essential ($199/mo)",
  },
  {
    stage: "Seed ($50K - $200K MRR)",
    accounting: "Accrual basis + close by 5th",
    reporting: "Monthly close by 5th + Board-ready deck",
    payroll: "Multi-state payroll (Gusto/Rippling) + 1099s",
    tax: "Sales tax nexus monitoring (up to 5 states)",
    tools: ["QBO / Xero", "Stripe", "Ramp", "Gusto", "Carta"],
    plan: "Growth ($499/mo)",
    popular: true,
  },
  {
    stage: "Series A ($200K - $500K MRR)",
    accounting: "Multi-entity consolidation + ASC 606 waterfalls",
    reporting: "Departmental P&L (Eng, Sales, Mktg) + SaaS metrics",
    payroll: "Automated contractor & international payroll",
    tax: "15+ state sales tax filings & economic nexus tracking",
    tools: ["QBO Advanced / NetSuite", "Chargebee", "Avalara", "Brex"],
    plan: "Scale ($1,299/mo)",
  },
  {
    stage: "Series B+ ($500K+ MRR)",
    accounting: "ERP system migration & transfer pricing",
    reporting: "Weekly flash reports + custom live dashboards",
    payroll: "Global cross-border entities (US, UK, India)",
    tax: "Big 4 audit liaison & transfer pricing study",
    tools: ["NetSuite", "Sage Intacct", "Rippling", "Deel"],
    plan: "Custom Plan",
  },
];

const toolsByStage = [
  {
    stage: "Pre-Seed Stack",
    desc: "Clean foundation without software overspend",
    tools: ["Mercury", "Brex", "QuickBooks Online", "Gusto"],
  },
  {
    stage: "Seed Stack",
    desc: "Accrual metrics, rev rec & automated bill pay",
    tools: ["QuickBooks Online", "Gusto / Rippling", "Stripe", "Ramp", "Carta"],
  },
  {
    stage: "Series A Stack",
    desc: "Multi-entity consolidation & audit workpapers",
    tools: ["NetSuite / QBO Advanced", "Chargebee / Maxio", "Avalara", "Brex", "Bill.com"],
  },
];

export const Route = createFileRoute("/industries/startups")({
  head: () => ({
    meta: [
      { title: "Startup Accounting & Finance Ops: Pre-Seed to Series A | Finendra" },
      {
        name: "description",
        content:
          "Institutional-grade bookkeeping, close by the 5th, board decks, ASC 606 rev rec, and burn forecasting for venture-backed & bootstrapped founders. Plans from $199/mo.",
      },
      { property: "og:title", content: "Startup Accounting & Finance Ops | Finendra" },
      {
        property: "og:description",
        content:
          "Finance operations built for pre-seed to Series A startups: clean books, fast closes, and founder-friendly pricing from $199/mo.",
      },
      { property: "og:url", content: "https://finendra.com/industries/startups" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Startup Accounting & Finance Ops | Finendra" },
      {
        name: "twitter:description",
        content: "Finance operations built for pre-seed to Series A startups.",
      },
    ],
    links: [{ rel: "canonical", href: "https://finendra.com/industries/startups" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          generateServiceSchema({
            name: "Startup Finance Operations & Bookkeeping",
            description:
              "Finance operations for pre-seed to Series A startups: bookkeeping, monthly close by 5th, board packs, and burn modeling.",
            url: "https://finendra.com/industries/startups",
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(generateFaqSchema(startupFaqs)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          generateBreadcrumbSchema([
            { name: "Home", url: "https://finendra.com" },
            { name: "Industries", url: "https://finendra.com/industries" },
            {
              name: "Startups (Pre-Seed to Series A)",
              url: "https://finendra.com/industries/startups",
            },
          ]),
        ),
      },
    ],
  }),
  component: StartupsPage,
});

function StartupsPage() {
  return (
    <>
      {/* Hero */}
      <section className="px-6 pb-20 pt-24 md:pt-32 border-b border-brand-navy/5 bg-gradient-to-b from-brand-surface/70 to-background">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/15 px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wider text-brand-navy">
                  <Sparkles className="size-3.5 text-brand-accent" />
                  Dedicated Startup Hub
                </div>

                <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-6xl">
                  Finance Operations Built for{" "}
                  <span className="text-brand-accent">Pre-Seed to Series A.</span>
                </h1>

                <p className="mt-6 max-w-2xl text-lg text-brand-navy/70 leading-relaxed">
                  Clean books from day one, monthly close by the 5th, and investor-ready board
                  packs. We are bootstrapped ourselves — we price so early-stage founders get
                  institutional accounting without burning their seed round.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Link
                    to="/contact"
                    search={{ plan: "essential", industry: "startups" }}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-8 py-4 font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-brand-accent hover:shadow-xl active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
                  >
                    Start with Essential ($199/mo) <ArrowRight className="size-4" />
                  </Link>
                  <a
                    href="#stage-matrix"
                    className="rounded-xl border border-brand-navy/15 bg-background px-6 py-4 font-semibold text-brand-navy transition-[background-color,border-color,transform] duration-150 ease-out hover:bg-brand-surface active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
                  >
                    See What Changes by Stage
                  </a>
                </div>

                {/* 3 Anonymized Proof Tags */}
                <div className="mt-10 flex flex-wrap items-center gap-3 pt-6 border-t border-brand-navy/10 text-xs font-semibold text-brand-navy/80">
                  <span className="rounded-full bg-brand-surface border border-brand-navy/10 px-3.5 py-1.5 shadow-sm">
                    B2B SaaS • $4M ARR
                  </span>
                  <span className="rounded-full bg-brand-surface border border-brand-navy/10 px-3.5 py-1.5 shadow-sm">
                    DTC Brand • $8M GMV
                  </span>
                  <span className="rounded-full bg-brand-surface border border-brand-navy/10 px-3.5 py-1.5 shadow-sm">
                    Research Nonprofit • $6M Revenue
                  </span>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-4">
              <Reveal delay={0.15}>
                <div className="rounded-3xl border border-brand-navy/10 bg-brand-navy p-8 text-background shadow-2xl">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                      Founder Standard
                    </span>
                    <span className="rounded-full bg-brand-accent/20 px-2.5 py-0.5 text-[0.65rem] font-bold text-brand-accent">
                      12+ Startups
                    </span>
                  </div>

                  <div className="mt-6 font-display text-4xl font-extrabold text-background">
                    Close by 5th
                  </div>
                  <p className="mt-1 text-sm text-background/80">Every single month.</p>

                  <ul className="mt-6 space-y-3.5 border-t border-background/10 pt-6 text-xs text-background/80">
                    <li className="flex items-center gap-2.5">
                      <ShieldCheck className="size-4 text-brand-accent shrink-0" />
                      ASC 606 revenue recognition & deferred waterfalls
                    </li>
                    <li className="flex items-center gap-2.5">
                      <ShieldCheck className="size-4 text-brand-accent shrink-0" />
                      Burn rate, runway, and cash forecast included
                    </li>
                    <li className="flex items-center gap-2.5">
                      <ShieldCheck className="size-4 text-brand-accent shrink-0" />
                      Delaware C-Corp, Gusto & Mercury integration
                    </li>
                    <li className="flex items-center gap-2.5">
                      <ShieldCheck className="size-4 text-brand-accent shrink-0" />
                      Month-to-month • Pause if runway gets tight
                    </li>
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* What Changes at Each Stage Comparison Matrix */}
      <section id="stage-matrix" className="px-6 py-24 bg-background">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Progression Framework
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              What changes at each stage of company growth
            </h2>
            <p className="mt-4 text-base text-brand-navy/60 leading-relaxed">
              Accounting isn't one-size-fits-all. As your revenue and employee count climb, your
              compliance, rev rec, and board requirements transition. Here is how Finendra scales
              with you:
            </p>
          </Reveal>

          <div className="mt-12 overflow-x-auto rounded-3xl border border-brand-navy/10 bg-background shadow-sm">
            <table className="w-full min-w-[54rem] border-collapse text-left">
              <thead>
                <tr className="border-b border-brand-navy/10 bg-brand-surface/60">
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Stage
                  </th>
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Accounting Basis
                  </th>
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Reporting Cadence
                  </th>
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Payroll & Tax
                  </th>
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Recommended Plan
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-navy/10 text-sm">
                {stageMatrix.map((row) => (
                  <tr
                    key={row.stage}
                    className={`hover:bg-brand-surface/40 transition-colors ${
                      row.popular ? "bg-brand-accent/5 font-medium" : ""
                    }`}
                  >
                    <td className="px-6 py-6 font-bold text-brand-navy">
                      {row.stage}
                      {row.popular && (
                        <span className="ml-2 rounded-full bg-brand-accent px-2 py-0.5 text-[0.65rem] font-bold text-background uppercase">
                          Most Common
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-6 text-brand-navy/80">{row.accounting}</td>
                    <td className="px-6 py-6 text-brand-navy/80">{row.reporting}</td>
                    <td className="px-6 py-6 text-brand-navy/80">{row.payroll}</td>
                    <td className="px-6 py-6">
                      <span className="inline-block rounded-xl bg-brand-navy px-3.5 py-1.5 text-xs font-bold text-background">
                        {row.plan}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Tools by Stage */}
      <section className="px-6 py-20 bg-brand-surface/40 border-y border-brand-navy/5">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Technology Stack
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Recommended finance stack by stage
            </h2>
            <p className="mt-4 text-base text-brand-navy/60">
              We connect directly into these platforms. No migration hurdles or software reseller
              markups.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {toolsByStage.map((group, idx) => (
              <Reveal key={group.stage} delay={idx * 0.1}>
                <div className="h-full rounded-3xl border border-brand-navy/10 bg-background p-8 shadow-sm">
                  <div className="flex items-center gap-2 text-brand-accent font-bold text-xs uppercase tracking-wider">
                    <Layers className="size-4" />
                    {group.stage}
                  </div>
                  <h3 className="mt-2 text-base font-semibold text-brand-navy">{group.desc}</h3>

                  <div className="mt-6 flex flex-wrap gap-2 pt-4 border-t border-brand-navy/10">
                    {group.tools.map((t) => (
                      <span
                        key={t}
                        className="rounded-xl border border-brand-navy/10 bg-brand-surface px-3 py-1.5 text-xs font-bold text-brand-navy"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Startup Pricing Callout */}
      <StartupPricingCallout slug="startups" />

      {/* Founder Testimonial Highlight */}
      <section className="px-6 py-20 bg-brand-navy text-background">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Founder Validation
            </span>
            <blockquote className="mt-6 font-display text-2xl md:text-3xl font-semibold leading-relaxed text-background">
              "Set up our books day 1 post-incorporation. When we raised seed, due diligence took 2
              days — clean as a whistle. There's no other team offering this level of precision for
              $199–$499/mo."
            </blockquote>
            <p className="mt-6 text-base font-bold text-brand-accent">Founder, YC W24 SaaS</p>
            <p className="text-xs text-background/60">
              Stage: Pre-seed → Seed • Plan: Essential → Growth
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founder FAQs */}
      <section className="px-6 py-24 bg-background">
        <div className="mx-auto max-w-3xl">
          <Reveal className="mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Startup FAQs
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Questions early-stage founders ask us
            </h2>
            <p className="mt-3 text-base text-brand-navy/60">
              Clear answers on runway, Delaware entity setup, CPA collaboration, and rev rec.
            </p>
          </Reveal>

          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {startupFaqs.map((f, i) => (
                <AccordionItem key={f.q} value={`startup-faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold text-brand-navy hover:text-brand-accent">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-brand-navy/70">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Lead Magnet */}
      <section className="px-6 py-12">
        <LeadMagnet defaultIndustry="Startups (Pre-seed / Seed)" />
      </section>

      {/* Bottom CTA */}
      <section className="px-6 py-20 bg-brand-surface/70 border-t border-brand-navy/5 text-center">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold text-brand-navy sm:text-4xl">
              Focus on product and fundraising. We'll own the books.
            </h2>
            <p className="mt-4 text-lg text-brand-navy/60">
              Book a 20-minute diagnostic with Deepak (Founder). Month-to-month, transparent
              pricing, zero lock-in.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                search={{ plan: "starter", industry: "startups" }}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-8 py-4 font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-accent hover:shadow-xl active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
              >
                Book Founder Diagnostic <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/pricing"
                className="rounded-xl border border-brand-navy/15 bg-background px-8 py-4 font-semibold text-brand-navy transition-[background-color,border-color,transform] duration-150 ease-out hover:bg-brand-surface active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
              >
                Compare Pricing Plans
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
